import React from 'react';
import { Box, IconButton, Text, VStack, HStack, Flex, Circle } from '@chakra-ui/react';
import { FiX, FiMic, FiMicOff } from 'react-icons/fi';
import { trackingService } from '../../../../shared/eventTracking';

interface ReadyToRecordProps {
  isRecording: boolean;
  audioData: number[];
  recordingTime: number;
  isTimerBlinking: boolean;
  isPaused: boolean;
  toggleRecording: () => void;
  cancelRecording: () => void;
  isMuted: boolean;
  toggleMute: () => void;
  availableMicrophones: MediaDeviceInfo[];
  selectedMicrophone: string;
  switchMicrophone: (deviceId: string) => void;
  showMicrophoneMenu: boolean;
  setShowMicrophoneMenu: (show: boolean) => void;
  formatTime: (seconds: number) => string;
  isMobile?: boolean;
}

function ReadyToRecord({
  isRecording,
  audioData,
  recordingTime,
  isTimerBlinking,
  isPaused,
  toggleRecording,
  cancelRecording,
  isMuted,
  toggleMute,
  availableMicrophones,
  selectedMicrophone,
  switchMicrophone,
  showMicrophoneMenu,
  setShowMicrophoneMenu,
  formatTime,
  isMobile = false,
}: ReadyToRecordProps) {
  return (
    <>
      {isRecording && (
        <Box px="0" pb="16px">
          <Flex justify="space-between" align="center" h="80px" px="16px">
            {audioData.slice(0, 20).map((value, index) => {
              const normalizedValue = Math.max(2, Math.pow(value / 255, 0.7) * 40);
              return (
                <Box key={index} position="relative" width="6px" height="80px">
                  <Box
                    position="absolute"
                    top="50%"
                    left="0"
                    right="0"
                    height={`${normalizedValue}px`}
                    bg="purple.600"
                    borderRadius="3px"
                    transform="translateY(-100%)"
                    transition="height 0.05s ease-out"
                    minHeight="2px"
                  />
                  <Box
                    position="absolute"
                    top="50%"
                    left="0"
                    right="0"
                    height={`${normalizedValue}px`}
                    bg="purple.600"
                    borderRadius="3px"
                    transform="translateY(0%)"
                    transition="height 0.05s ease-out"
                    minHeight="2px"
                  />
                </Box>
              );
            })}
          </Flex>
        </Box>
      )}

      <VStack spacing={isMobile ? '12px' : '8px'} align="center" py={isMobile ? '30px' : '20px'}>
        <Text
          fontSize={isMobile ? '32px' : '18px'}
          fontWeight="bold"
          color={isTimerBlinking ? 'red.500' : 'purple.600'}
          className={isTimerBlinking ? 'timer-blink-animation' : ''}
        >
          {formatTime(recordingTime)}
        </Text>
        <Text fontSize={isMobile ? '18px' : '14px'} color="gray.500">
          {isPaused ? 'Pausado' : 'Ouvindo'}
        </Text>
      </VStack>

      <Flex justify="center" align="center" pb={isMobile ? '30px' : '20px'}>
        <Box position="relative">
          <Circle
            size={isMobile ? '100px' : '70px'}
            border={isMobile ? '4px solid' : '3px solid'}
            borderColor="purple.200"
            bg="purple.200"
            position="absolute"
            top={isMobile ? '-7px' : '-5px'}
            left={isMobile ? '-7px' : '-5px'}
            className="primary-pulse-animation"
          />
          <Circle
            size={isMobile ? '86px' : '60px'}
            bg="purple.600"
            cursor="pointer"
            _hover={{ bg: 'purple.700' }}
            onClick={toggleRecording}
            boxShadow="0 4px 20px rgba(0, 0, 0, 0.15)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
            zIndex="1"
          >
            <Box bg="white" w={isMobile ? '28px' : '20px'} h={isMobile ? '28px' : '20px'} borderRadius="3px" />
          </Circle>
        </Box>
      </Flex>

      <Flex justify="center" align="center" pb={isMobile ? '30px' : '20px'}>
        <Box
          bg="white"
          borderRadius={isMobile ? '32px' : '24px'}
          border="1px solid"
          borderColor="gray.200"
          p={isMobile ? '12px' : '8px'}
          display="flex"
          alignItems="center"
          gap={isMobile ? '16px' : '12px'}
        >
          <IconButton
            aria-label="Cancelar"
            icon={<FiX size={isMobile ? '24px' : '16px'} />}
            size={isMobile ? 'lg' : 'md'}
            variant="ghost"
            bg="purple.200"
            color="purple.600"
            borderRadius="50%"
            _hover={{ bg: 'purple.300' }}
            onClick={cancelRecording}
            minW={isMobile ? '52px' : '40px'}
            h={isMobile ? '52px' : '40px'}
          />

          <Box
            bg="purple.200"
            borderRadius={isMobile ? '26px' : '20px'}
            p={isMobile ? '8px' : '6px'}
            display="flex"
            alignItems="center"
            gap={isMobile ? '8px' : '6px'}
            _hover={{ bg: 'purple.300' }}
            cursor="pointer"
          >
            <Box
              bg={isMuted ? 'red.100' : 'white'}
              borderRadius="50%"
              p={isMobile ? '10px' : '6px'}
              cursor="pointer"
              onClick={toggleMute}
              _hover={{ bg: isMuted ? 'red.200' : 'gray.50' }}
              minW={isMobile ? '40px' : '28px'}
              h={isMobile ? '40px' : '28px'}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {isMuted ? (
                <FiMicOff color="#E53E3E" size={isMobile ? '20px' : '14px'} />
              ) : (
                <FiMic color="#805AD5" size={isMobile ? '20px' : '14px'} />
              )}
            </Box>

            <Box
              position="relative"
              bg="white"
              borderRadius="50%"
              p={isMobile ? '10px' : '6px'}
              cursor="pointer"
              _hover={{ bg: 'gray.50' }}
              mr={isMobile ? '4px' : '2px'}
              minW={isMobile ? '40px' : '28px'}
              h={isMobile ? '40px' : '28px'}
              display="flex"
              alignItems="center"
              justifyContent="center"
              onClick={() => {
                if (!showMicrophoneMenu) {
                  trackingService.trackPrescriptionVoiceMicrophoneMenu();
                }
                setShowMicrophoneMenu(!showMicrophoneMenu);
              }}
            >
              <Text fontSize={isMobile ? '20px' : '14px'} color="#805AD5">
                ⋯
              </Text>

              {showMicrophoneMenu && (
                <Box
                  data-microphone-menu
                  position="absolute"
                  bottom="40px"
                  right={isMobile ? 'auto' : '0'}
                  left={isMobile ? '50%' : 'auto'}
                  transform={isMobile ? 'translateX(-50%)' : 'none'}
                  bg="white"
                  borderRadius="12px"
                  boxShadow="0 8px 32px rgba(0, 0, 0, 0.12)"
                  border="1px solid"
                  borderColor="gray.200"
                  minW="300px"
                  maxW="400px"
                  zIndex="1000"
                >
                  <Box p="16px" borderBottom="1px solid" borderColor="gray.100">
                    <HStack spacing="8px">
                      <Text fontSize="16px" fontWeight="600" color="purple.600">
                        Alternar
                      </Text>
                    </HStack>
                  </Box>

                  <Box maxH="200px" overflowY="auto">
                    {availableMicrophones.map((microphone, index) => (
                      <Box
                        key={microphone.deviceId}
                        p="12px 16px"
                        cursor="pointer"
                        _hover={{ bg: 'purple.50' }}
                        onClick={() => switchMicrophone(microphone.deviceId)}
                        borderBottom={index < availableMicrophones.length - 1 ? '1px solid' : 'none'}
                        borderBottomColor="gray.100"
                      >
                        <HStack spacing="12px">
                          {selectedMicrophone === microphone.deviceId && (
                            <Box color="purple.600" fontSize="14px">
                              ✓
                            </Box>
                          )}
                          <VStack align="start" spacing="2px" flex="1">
                            <Text fontSize="14px" fontWeight="500" color="gray.800">
                              {microphone.label || `Microfone ${index + 1}`}
                            </Text>
                            {microphone.label && (
                              <Text fontSize="12px" color="gray.500">
                                {microphone.label}
                              </Text>
                            )}
                          </VStack>
                        </HStack>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Flex>
    </>
  );
}

export { ReadyToRecord };
